import React, { FC, useEffect } from "react";

import polyline from "@mapbox/polyline";

import MarkerShape from "@mapmycustomers/shared/enum/MarkerShape";
import MarkerSize from "@mapmycustomers/shared/enum/MarkerSize";
import { Trip } from "@mapmycustomers/shared/types/entity";

import colors from "@app/styles/_colors.module.scss";
import useAnalytics from "@app/util/contexts/useAnalytics";
import useMap from "@app/util/hook/map/useMap";
import useMapEventHandler from "@app/util/hook/map/useMapEventHandler";
import getResultingSvg from "@app/util/map/markerStyles/getResultingSvg";
import { getScaledSizeByMarker } from "@app/util/map/markerStyles/getScaledSize";
import getSvgForShape from "@app/util/map/markerStyles/getSvgForShape";
import svgToDataUrl from "@app/util/map/svgToDataUrl";

import styles from "./Map.module.scss";

const getTripMarkerIcon = (shape: MarkerShape) => {
  const icon = getSvgForShape(shape, colors.slate, colors.white);

  return {
    anchor: new google.maps.Point(...getScaledSizeByMarker(icon.shapeAnchor, MarkerSize.LARGE)),
    scaledSize: new google.maps.Size(...getScaledSizeByMarker(icon.shapeSize, MarkerSize.LARGE)),
    url: svgToDataUrl(getResultingSvg(icon.shapeSvg, icon.shapeSize, icon.shapeAnchor).svg),
  };
};

export interface MapProps {
  trip: Trip;
}

const Map: FC<MapProps> = ({ trip }) => {
  const { map, mapElementId } = useMap({
    center: { lat: 0, lng: 0 },
    controlSize: 32,
    gestureHandling: "greedy",
    mapTypeControl: true,
    zoom: 2,
    zoomControl: true,
  });

  const analytics = useAnalytics();
  useMapEventHandler(map, "center_changed", () => analytics.using(["Map"]));
  useMapEventHandler(map, "zoom_changed", () => analytics.using(["Map"]));
  useMapEventHandler(map, "click", () => analytics.using(["Map"]));

  const line = trip.polyline ? polyline.decode(trip.polyline) : undefined;

  useEffect(() => {
    if (!map || !line || line.length < 2) {
      return;
    }

    const mapPolyline = new google.maps.Polyline({
      map,
      path: line.map(([lat, lng]) => ({ lat, lng })),
      strokeColor: colors.primary100,
    });

    const bounds = new google.maps.LatLngBounds();
    mapPolyline.getPath().forEach((point) => bounds.extend(point));
    map.fitBounds(bounds);

    const startMarker = new google.maps.Marker({
      icon: getTripMarkerIcon(MarkerShape.TRIP_START),
      map,
      position: { lat: line[0][0], lng: line[0][1] },
    });

    const endMarker = new google.maps.Marker({
      icon: getTripMarkerIcon(MarkerShape.TRIP_FINISH),
      map,
      position: { lat: line.at(-1)![0], lng: line.at(-1)![1] },
    });

    return () => {
      endMarker.setMap(null);
      startMarker.setMap(null);
      mapPolyline.setMap(null);
    };
  }, [line, map]);

  if (!line || line.length < 2) {
    return null;
  }

  return <div className={styles.container} id={mapElementId} />;
};

export default Map;
